/**
 * An append-only ledger, in the shape Cleanify already proved.
 *
 * Deliberately the same shape: deterministic IDs, immutable entries,
 * corrections as new entries rather than edits, balances as projections of the
 * whole history. That is not imitation — it is what lets this module drop into
 * `cleanify/src/domain/` if Fortunaut ends up living there, and what makes an
 * offline device converge instead of argue.
 *
 * One thing here has no Cleanify equivalent, and it is the gap this prototype
 * exists to close: **settlement**. Money coming in was fully modelled in the
 * plan; money actually leaving was not. Nothing represented the moment a parent
 * hands over $12 in cash. Without it balances only ever grow, the ledger drifts
 * from reality, and the parent has no honest answer to "what do I owe?"
 */

import { allocate } from './money.js';

export const BUCKETS = Object.freeze(['spend', 'save', 'give']);

export const REASON_TYPES = Object.freeze([
  'allowance',
  'gold_conversion',
  'interest',
  'match',
  'deposit',
  'allocation',
  'settlement',
  'reversal',
  'opening_balance',
]);

export const SETTLEMENT_METHODS = Object.freeze(['cash', 'purchased', 'transferred', 'donated']);

export function createLedger(entries = []) {
  const byId = new Map(entries.map((e) => [e.id, e]));

  const append = (entry) => {
    if (!entry?.id) throw new Error('ledger entry needs a stable id');
    // First write wins. A retry is a no-op, not a second helping of money.
    if (byId.has(entry.id)) return { entry: byId.get(entry.id), added: false };
    byId.set(entry.id, entry);
    return { entry, added: true };
  };

  return {
    append,
    appendAll: (list) => list.map(append),
    has: (id) => byId.has(id),
    ids: () => new Set(byId.keys()),
    all: () => [...byId.values()].sort(
      (a, b) => String(a.occurredAt).localeCompare(String(b.occurredAt)) || a.id.localeCompare(b.id),
    ),
  };
}

/**
 * Balances are a deterministic sum of history, never a stored counter.
 *
 * Unbucketed income sits in `unallocated` until the child decides where it
 * goes — the allocation *is* the product's core lesson, so the app must not
 * quietly make the choice for them by defaulting it into Spend.
 */
export function project(entries, { childId = null } = {}) {
  const buckets = { spend: 0, save: 0, give: 0 };
  let unallocated = 0;

  for (const e of entries) {
    if (e.currency !== 'usd') continue;
    // The firewall between the two ledgers, enforced structurally rather than by
    // whoever assembled the array. Family envelope money has no owner — it is
    // the household's — so an entry without a `childId` is not a child's money
    // and must never reach a child's balance. Without this line, handing
    // `project` a mixed array of child and Fuel entries silently credits the
    // family's fun budget to whichever child's screen is open, and the first
    // symptom is a child reading a balance the parent never granted.
    if (!e.childId) continue;
    if (childId && e.childId !== childId) continue;
    if (e.reasonType === 'allocation') {
      unallocated -= e.amountCents;
      buckets[e.bucket] += e.amountCents;
    } else if (e.bucket) {
      buckets[e.bucket] += e.amountCents;
    } else {
      unallocated += e.amountCents;
    }
  }

  const total = unallocated + buckets.spend + buckets.save + buckets.give;
  return { ...buckets, unallocated, total };
}

/**
 * Split unallocated money across buckets.
 *
 * Uses largest-remainder allocation so three-way splits of odd amounts still
 * sum to exactly what went in — a child who allocates $10 has $10 afterwards.
 */
export function allocationEntries({ childId, cents, weights, occurredAt, reasonId }) {
  const shares = allocate(cents, BUCKETS.map((b) => weights[b] ?? 0));
  return BUCKETS.map((bucket, i) => ({
    id: `allocation:${childId}:${reasonId}:${bucket}`,
    childId,
    currency: 'usd',
    bucket,
    amountCents: shares[i],
    reasonType: 'allocation',
    reasonId,
    occurredAt,
  })).filter((e) => e.amountCents > 0);
}

/**
 * Money a parent puts into a child's ledger by hand.
 *
 * Birthday cash, a grandparent's cheque, the tooth fairy, the balance that was
 * already in the jar before the app existed — all the income the formula never
 * produces. Without it the ledger is a partial account of the child's money,
 * and a partial account is one a family stops trusting the moment it disagrees
 * with the envelope on the shelf.
 *
 * `bucket` is null on purpose. Deposited money lands unallocated and the child
 * decides where it goes, for the reason `project` gives above: the allocation
 * is the lesson, and defaulting it into Spend quietly makes the choice for
 * them. A parent who wants it saved says so with an allocation entry, which is
 * a second, visible act.
 *
 * The id is keyed on the caller's `depositId`, so a double-tapped Add Money —
 * or a retried request whose response was lost — deposits once.
 */
export function depositEntry({ childId, cents, actorId, note, depositId, occurredAt }) {
  if (!childId) throw new Error('a deposit needs a childId');
  if (!depositId) throw new Error('a deposit needs a stable depositId');
  if (!Number.isInteger(cents)) throw new Error('deposits are whole cents; got ' + cents);
  // A negative or zero deposit is a different event wearing a deposit's name:
  // taking money back is a reversal (with a reason), and nothing is not an
  // entry. Silently accepting either would let a typo debit a child.
  if (cents <= 0) throw new Error('a deposit must be a positive number of cents');

  return {
    id: `deposit:${childId}:${depositId}`,
    childId,
    currency: 'usd',
    bucket: null,
    amountCents: cents,
    reasonType: 'deposit',
    reasonId: depositId,
    actorId,
    note,
    occurredAt,
  };
}

/**
 * Value leaving the ledger because a parent actually handed it over.
 *
 * `remainderCents` is the honest half of settling in cash: $4.37 owed and $4.25
 * in coins on the counter is not a rounding error to swallow, it is 12¢ the
 * child is still owed. It stays on the ledger.
 */
export function settlementEntry({
  childId,
  bucket,
  owedCents,
  paidCents,
  method = 'cash',
  actorId,
  note,
  requestId,
  occurredAt,
}) {
  if (!SETTLEMENT_METHODS.includes(method)) throw new Error(`unknown settlement method: ${method}`);
  const applied = Math.min(paidCents, owedCents);
  return {
    entry: {
      id: `settlement:${childId}:${requestId}`,
      childId,
      currency: 'usd',
      bucket,
      amountCents: -applied,
      reasonType: 'settlement',
      reasonId: requestId,
      occurredAt,
      actorId,
      note,
      snapshot: { method, owedCents, paidCents },
    },
    remainderCents: owedCents - applied,
  };
}

/** Corrections are new entries pointing at old ones. History is never edited. */
export function reversalEntry({ original, actorId, reason, occurredAt }) {
  if (!reason) throw new Error('a reversal needs a visible reason');
  return {
    id: `reversal:${original.id}`,
    childId: original.childId,
    currency: original.currency,
    bucket: original.bucket,
    amountCents: -original.amountCents,
    reasonType: 'reversal',
    reversesId: original.id,
    actorId,
    note: reason,
    occurredAt,
  };
}
